import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const NAVY = [22, 32, 106];
const CYAN = [0, 168, 230];
const GRAY = [70, 70, 70];
const LOGO_SRC = `${process.env.PUBLIC_URL || ""}/logo-refriactive.png`;

const COMPANY = {
  form: "FO-GFI-02",
  version: "V 01",
  address: ["CLL 54 # 85-40", "Oficina 2117", "Medellin", "Ant, Colombia"],
  nit: "901055057-4 Régimen común",
  pbx: "301 6891901/302 2371005",
  web: "https://www.refriactivebyg.com",
};

const money = (value) => {
  const n = Number(value);
  if (Number.isNaN(n)) return "$ 0.0";
  return `$ ${n.toLocaleString("en-US", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}`;
};

const textOrEmpty = (value) => {
  if (value === null || value === undefined) return "";
  return String(value);
};

const loadLogo = () =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext("2d").drawImage(img, 0, 0);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = () => resolve(null);
    img.src = LOGO_SRC;
  });

const drawPill = (doc, x, y, w, label) => {
  doc.setFillColor(...NAVY);
  doc.roundedRect(x, y, w, 5.4, 1.6, 1.6, "F");
  doc.setFillColor(255, 255, 255);
  doc.circle(x + 2.7, y + 2.7, 1.55, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.text(label, x + 5.4, y + 3.6);
  doc.setTextColor(30, 30, 30);
};

const drawField = (doc, x, y, w, label, value) => {
  drawPill(doc, x, y, w, label);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...GRAY);
  const lines = doc.splitTextToSize(textOrEmpty(value) || " ", w - 2);
  doc.text(lines, x + 1, y + 9.2);
  return 7 + lines.length * 4.2;
};

const drawHeader = (doc, quote, logo, page, pages) => {
  if (logo) {
    doc.addImage(logo, "PNG", 16, 10, 32, 32);
  }
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(...GRAY);
  doc.text(COMPANY.form, 32, 45, { align: "center" });
  doc.text(COMPANY.version, 32, 48.5, { align: "center" });

  doc.setFontSize(8);
  doc.text(`Página ${page} / ${pages}`, 198, 12, { align: "right" });

  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.setTextColor(...NAVY);
  doc.text(`Cotización # ${quote.id ?? ""}`, 198, 22, { align: "right" });
};

const drawFooter = (doc) => {
  const y = 276;
  doc.setDrawColor(...NAVY);
  doc.setLineWidth(0.4);
  doc.line(14, y, 196, y);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...GRAY);
  doc.text("Dirección", 16, y + 5);
  COMPANY.address.forEach((line, i) => {
    doc.text(line, 16, y + 9 + i * 3.6);
  });
  doc.text("NIT", 128, y + 5);
  doc.text(COMPANY.nit, 138, y + 5);
  doc.text("PBX", 128, y + 9);
  doc.text(COMPANY.pbx, 138, y + 9);
  doc.text("Web", 128, y + 13);
  doc.text(COMPANY.web, 138, y + 13);
};

export const downloadQuotePdf = async (quote) => {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const logo = await loadLogo();
  const pageWidth = 210;
  const margin = 14;
  const inner = pageWidth - margin * 2;
  const col = (inner - 4) / 2;

  const phone = quote.phone
    ? String(quote.phone).startsWith("+")
      ? quote.phone
      : `+57 ${quote.phone}`
    : "";
  const description =
    quote.description ||
    "SERVICIO A EQUIPOS DE REFRIGERACION Y AIRES ACONDICIONADOS.";

  const drawChrome = (page, pages) => {
    drawHeader(doc, quote, logo, page, pages);
    drawFooter(doc);
  };

  let y = 54;
  const row1 = Math.max(
    drawField(doc, margin, y, col, "Fecha de solicitud", quote.aplicationDate),
    drawField(doc, margin + col + 4, y, col, "Ciudad", quote.cityId)
  );
  y += row1;
  const row2 = Math.max(
    drawField(doc, margin, y, col, "Cliente", quote.clientId),
    drawField(doc, margin + col + 4, y, col, "Almacen", quote.storeId)
  );
  y += row2;
  const row3 = Math.max(
    drawField(doc, margin, y, col, "Supervisor", quote.supervisor),
    drawField(doc, margin + col + 4, y, col, "Teléfono", phone)
  );
  y += row3;
  y += drawField(doc, margin, y, inner, "Email", quote.email);
  y += drawField(doc, margin, y, inner, "Descripción", description) + 2;

  const parts = Array.isArray(quote.quoteParts) ? quote.quoteParts : [];
  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin, top: 52, bottom: 28 },
    head: [["Item", "Descripción", "Unidad", "Valor unitario", "Cantidad", "Valor total"]],
    body: parts.map((part) => [
      textOrEmpty(part.item),
      textOrEmpty(part.description),
      textOrEmpty(part.unidad),
      money(part.costUnit),
      textOrEmpty(part.quantity),
      money(part.totalCost),
    ]),
    styles: {
      font: "helvetica",
      fontSize: 8,
      cellPadding: 2.2,
      textColor: [40, 40, 40],
      lineColor: [230, 230, 230],
      lineWidth: 0.1,
    },
    headStyles: {
      fillColor: NAVY,
      textColor: 255,
      fontStyle: "bold",
      halign: "center",
    },
    columnStyles: {
      0: { cellWidth: 14, halign: "center" },
      1: { cellWidth: 78 },
      2: { cellWidth: 18, halign: "center" },
      3: { cellWidth: 28, halign: "right" },
      4: { cellWidth: 20, halign: "center" },
      5: { cellWidth: 24, halign: "right" },
    },
    theme: "plain",
  });

  let totalsY = (doc.lastAutoTable?.finalY || y) + 8;
  if (totalsY > 230) {
    doc.addPage();
    totalsY = 56;
  }

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...GRAY);
  doc.text("Subtotal", 150, totalsY, { align: "right" });
  doc.text(money(quote.subtotal), 196, totalsY, { align: "right" });
  doc.text("IVA 19%", 150, totalsY + 6, { align: "right" });
  doc.text(money(quote.iva), 196, totalsY + 6, { align: "right" });

  const barY = totalsY + 11;
  doc.setFillColor(...CYAN);
  doc.rect(margin, barY, inner, 16, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("Gracias por confiar en nosotros", margin + 6, barY + 10);
  doc.setFontSize(9);
  doc.text("Total", 168, barY + 5.5, { align: "right" });
  doc.setFontSize(16);
  doc.text(money(quote.total), 196, barY + 12, { align: "right" });

  let condY = barY + 22;
  if (condY > 235) {
    doc.addPage();
    condY = 56;
  }
  const condRow1 = Math.max(
    drawField(doc, margin, condY, col, "Tiempo de entrega", quote.deliveryTime),
    drawField(doc, margin + col + 4, condY, col, "Validez de la oferta", quote.offerValidity)
  );
  condY += condRow1;
  const condRow2 = Math.max(
    drawField(doc, margin, condY, col, "Forma de pago", quote.paymentMeth),
    drawField(doc, margin + col + 4, condY, col, "Garantía", quote.warranty)
  );
  condY += condRow2;
  drawField(
    doc,
    margin,
    condY,
    inner,
    "Observaciones",
    quote.observations === "Sin observaciones" ? "" : quote.observations
  );

  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i += 1) {
    doc.setPage(i);
    drawChrome(i, pages);
  }

  doc.save(`Cotización #${quote.id}.pdf`);
};
